<template>
  <div class="course-video">
    <div class="course-video__details">
      <div class="toggle-icons">
        <div class="toggle-icons__mark-as-finish">
          <span class="toggle-icons__label">MARK FINISHED</span>
          <SwitchCheckbox
            :disabled="isLoading"
            v-model="isFinished"
          ></SwitchCheckbox>
          <LoadingSmallSpinner v-if="isLoading" />
        </div>
        <div class="course-video__report-issue" @click.stop="startIssue">
          <span class="course-video__report-issue__text">Report an issue</span>
        </div>
      </div>
    </div>
    <div v-if="isWatching" class="course-video__player-wrapper">
      <MediaDelegate
        v-bind="{
          parentType: parentType,
          content: { ...video, type: 'video' },
          nextMedia: nextContent,
          userCompletionStatus: videoStatus,
        }"
        ref="mediaDelegate"
        @advance-media="playNextContent"
        @is-loading="updateLoader"
      />
      <div
        class="course-video__attachments"
        v-if="video.files && video.files.length"
      >
        <span class="course-video__attachments-label"
          >Video attachment(s):</span
        >
        <Files
          class="video-files"
          :content="video"
          :canOpen="false"
          :transparent="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import MediaDelegate from '@/components/media/MediaDelegate.vue'
import Files from '@/components/modals/exercise-modal/Files.vue'
import CONSTANTS from '@/constants'
import SwitchCheckbox from '@/components/forms/elements/SwitchCheckbox.vue'
import LoadingSmallSpinner from '@/components/loaders/LoadingSmallSpinner.vue'

export default {
  name: 'ContentVideo',
  components: {
    MediaDelegate,
    Files,
    SwitchCheckbox,
    LoadingSmallSpinner,
  },
  data() {
    return {
      isLoading: false,
    }
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    isWatching: {
      type: Boolean,
      required: true,
    },
    videoStatus: {
      type: String,
      required: true,
    },
    quizId: {
      type: String,
      required: false,
    },
    hasPassedQuiz: {
      type: Boolean,
      required: false,
    },
    nextVideo: {
      type: [Object, null],
      required: false,
    },
  },
  computed: {
    ...mapState({
      profile: state => state.auth.userProfile,
      currentParent: state => state.content.currentParent,
    }),
    nextContent() {
      if (this.quizId && this.quizId.length && !this.hasPassedQuiz) {
        return 'quiz'
      }
      if (this.nextVideo) {
        return 'video'
      }
      return ''
    },
    hasWatched() {
      return this.videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
    },
    isFinished: {
      get() {
        return this.hasWatched
      },
      set() {
        this.toggleVideoStatus()
      },
    },
    parentType() {
      return this.currentParent?.type
    },
  },
  methods: {
    updateLoader(e) {
      this.$emit('is-loading-video', e)
    },
    playNextContent() {
      const { nextContent, quizId, nextVideo } = this
      switch (nextContent) {
        case 'quiz':
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setCurrentQuizId', quizId)
          break
        case 'video':
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setPlayingVideoId', nextVideo.uuid)
          break
        default:
          break
      }
    },
    async toggleVideoStatus() {
      this.isLoading = true
      const {
        video: { id: videoId },
        videoStatus,
      } = this
      const newStatus =
        videoStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
          ? CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      await this.$store.dispatch('content/updateVideoStatus', {
        videoId,
        status: newStatus,
      })
      await this.$store.dispatch('content/getVideoStatus', videoId)
      if (
        this.currentParent?.type === CONSTANTS.PARENT_CONTENT_TYPES_KEYS.COURSE
      ) {
        setTimeout(async () => {
          await this.$store.dispatch(
            'courses/getCourseById',
            this.currentParent?.id
          )
        }, 500)
      }
      this.isLoading = false
    },
    startIssue() {
      this.$store.dispatch('content/saveContentIssue', {
        content_type: 'video',
        content_subtype: this.video.type,
        content_name: this.video.name,
        content_id: this.video.id,
        extra_args: {
          video_player_time: this.$refs.mediaDelegate.$refs.currentVideo
            .currentTime,
          video_player_status: this.videoStatus,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.course-video {
  $this: &;
  margin: 0 auto 0 0;
  transition: 1s all ease;
  position: relative;
  &__details {
    display: flex;
    align-items: center;
    margin: 20px 0 20px 0;
  }
  &__icon-wrapper {
    height: 62px;
    margin: 0 5px 0 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;

    .btn__switch {
      margin: 0px 10px;
    }
  }
  &__loading-wrapper {
    width: 62px;
    height: 62px;
    text-align: center;
  }
  &__loading-spinner {
    margin: 5px 0 0 0;
  }
  &__icon {
    vertical-align: middle;
  }
  &__duration-wrapper {
    display: flex;
    align-items: center;
    margin: 5px 0 0 50px;
  }
  &__duration {
    color: var(--white);
  }
  .toggle-icons {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    @include mobile {
      width: 100%;
      justify-content: initial;
    }
    &__check-wrapper {
      display: flex;
    }
    &__check {
      margin-left: 20px;
    }
    &__label {
      line-height: 0;
    }
    &__mark-as-finish {
      background: transparent;
      color: var(--white);
      font-weight: 700;
      border: none;
      padding: 0px;
      cursor: pointer;
      display: flex;
      align-items: center;
    }
    .btn__switch {
      margin: 0px 10px;
    }
  }
  &__report-issue {
    color: var(--light-blue-gray);
    margin-left: auto;
    display: flex;
    align-items: center;
    cursor: pointer;
    text-decoration: underline;
    &:hover {
      color: var(--white);
    }
  }

  &__attachments {
    color: var(--white);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
  }

  &__attachments-label {
    margin: 5px 0px;
  }

  .video-files {
    flex: none;
    order: 2;
    align-self: stretch;
    flex-grow: 0;
    margin: 5px 0px;
    padding: 0px;

    .file {
      $file: &;
      background: var(--glass-mid-gray);
      padding: 8px 15px;
      position: static;
      height: 52px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &:hover {
        background: linear-gradient(
          112.2deg,
          rgba(187, 188, 199, 0.4) -46.39%,
          rgba(187, 188, 199, 0) 112.38%
        );
        box-shadow: 0px 0px 1px rgba(255, 255, 255, 0.5);
      }
    }

    .accordion {
      flex: none;
      order: 1;
      align-self: stretch;
      flex-grow: 0;
      margin: 5px 0px;

      &:hover {
        .file {
          &__header {
            &-download-icon {
              background: #ffffff;
              box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
              border-radius: 5px;
              color: #1e1e1e;
            }

            &-status {
              &-wrapper {
                background: var(--white);
                box-shadow: 0px 4px 40px rgba(255, 255, 255, 0.4);
              }
            }
          }
        }
      }
    }
  }
}
</style>
